import React from 'react';
import { Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { OperationStatus } from 'components/operation/models/OperationModel';
import { actionExportsApi } from 'components/analytics/export/actionExportsApi';
import { MESSAGE_ACTION_EXPORT_FILE_DOWNLOAD_FAIL } from 'config/messages';
import { useSnackbar } from 'notistack';
import logger from 'lib/logger';
import { createDownloadLink } from 'lib/helpers';
import { GridCircularProgress } from 'lib/MuiGrid/GridCircularProgress';

interface IExportedDataActionCellProps {
    operation: any;
}

export const ExportedDataActionCell: React.FC<IExportedDataActionCellProps> = ({ operation }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = React.useState(false);
    const handleButtonClick = React.useCallback(
        async (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setLoading(true);

            try {
                const response = await actionExportsApi.getActionExportFile(operation.result?.href);
                if (!response.ok) {
                    throw new Error(response.statusText);
                }
                const blob = await response.blob();
                createDownloadLink(blob, 'actions.zip');
            } catch (e) {
                logger.error(e);
                enqueueSnackbar(MESSAGE_ACTION_EXPORT_FILE_DOWNLOAD_FAIL, { variant: 'error' });
            } finally {
                setLoading(false);
            }
        },
        [operation.result?.href, enqueueSnackbar]
    );
    const progress = React.useMemo(() => {
        if (operation.result && operation.result.total) {
            return (
                (operation.result.errors +
                    operation.result.exported +
                    operation.result.skipped +
                    (operation.result.removedUnlinked || 0)) /
                operation.result.total
            );
        }
        if (operation?.result?.total === 0) {
            return 1;
        }
        return undefined;
    }, [operation.result]);
    return (
        <Box display="flex" alignItems="center" justifyContent="center" width="100%" height="100%">
            <GridCircularProgress
                progress={isNaN(progress) ? -1 : progress * 100}
                isError={operation.status === OperationStatus.ERROR}
            >
                {operation.status === OperationStatus.DONE && operation.result && operation.result.href ? (
                    <Tooltip title="Download">
                        <IconButton
                            size="small"
                            color="primary"
                            disabled={loading}
                            onClick={handleButtonClick}
                        >
                            {loading ? <CircularProgress size={20} /> : <DownloadIcon fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                ) : (
                    <Box />
                )}
            </GridCircularProgress>
        </Box>
    );
};
